<script>
import moment from 'moment';

/* COMPOSANTS */
import AkDialogHeadless from '@components/general/AkDialogHeadless.vue';
import AkCheckbox from '@components/v2/input/AkCheckbox.vue';

const SUPPORT_PHONE_LINK = '+33743395737';
const CONSEQUENCES = ['fees', 'platform', 'ranking', 'review', 'relocation'];

function capitalizeName(name) {
  const trimmed = name?.trim();
  if (!trimmed) return undefined;
  return trimmed.toLowerCase().replace(/(^|[\s-])\p{L}/gu, (letter) => letter.toUpperCase());
}

export default {
  components: {AkDialogHeadless, AkCheckbox},
  props: {
    reservation: {type: Object, required: true},
  },
  emits: ['confirm'],
  data() {
    return {
      understood: false,
      showUnderstoodError: false,
      consequences: CONSEQUENCES,
    };
  },
  computed: {
    guestFirstName() {
      return capitalizeName(this.reservation.guestFirstName);
    },
    title() {
      if (!this.guestFirstName) return this.$t('reservation.cancel_request.title_no_name');
      return this.$t('reservation.cancel_request.title', {name: this.guestFirstName});
    },
    daysBeforeArrival() {
      return moment(this.reservation.dateStart).startOf('day').diff(moment().startOf('day'), 'days');
    },
    hasAccountManagerPhone() {
      return !!this.reservation.accountManagerPhone;
    },
    callLabel() {
      if (!this.hasAccountManagerPhone) return this.$t('reservation.cancel_request.call_guestadom');
      return this.$t('reservation.cancel_request.call', {
        name: capitalizeName(this.reservation.accountManagerFirstName),
      });
    },
    callHref() {
      const phone = this.hasAccountManagerPhone ? this.reservation.accountManagerPhone : SUPPORT_PHONE_LINK;
      return 'tel:' + phone.replace(/[^\d+]/g, '');
    },
  },
  methods: {
    show() {
      this.understood = false;
      this.showUnderstoodError = false;
      this.$refs.dialog.show();
    },
    hide() {
      this.$refs.dialog.hide();
    },
    changeUnderstood() {
      if (this.understood) this.showUnderstoodError = false;
    },
    confirm() {
      if (!this.understood) {
        this.showUnderstoodError = true;
        return;
      }
      this.$emit('confirm');
    },
  },
};
</script>

<template>
  <AkDialogHeadless
    ref="dialog"
    :show-cancel-button="false"
    padding-on-scrollbar
    :show-validate-button="false"
    width="600px">
    <template #header>
      <div class="flex items-start justify-between gap-4 w-full pb-4">
        <div class="flex flex-col gap-1.5">
          <span class="font-inter font-semibold text-xl text-grey-900">{{ title }}</span>
          <span class="self-start inline-flex items-center gap-1 rounded-full bg-red-100 pl-2 pr-2.5 py-0.5">
            <i class="ga-icon ga-clock text-base text-red" />
            <span class="font-inter font-semibold text-sm text-red">
              {{ $t('reservation.cancel_request.arrival', {n: daysBeforeArrival}, daysBeforeArrival) }}
            </span>
          </span>
        </div>
        <i class="ga-icon ga-supprimer text-3xl cursor-pointer hover:text-grey-700 text-grey-500" @click="hide" />
      </div>
    </template>

    <div class="flex flex-col gap-5 pb-5">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3">
        <div
          class="flex flex-row md:flex-col items-center md:items-start gap-3 md:gap-0.5 rounded-xl bg-red-100 px-4 py-3">
          <span class="font-inter font-bold text-3xl text-red min-w-[72px] md:min-w-0">
            {{ $t('reservation.cancel_request.cost_value') }}
          </span>
          <span class="font-inter font-medium text-sm text-grey-900">
            {{ $t('reservation.cancel_request.cost_label') }}
          </span>
        </div>
        <div
          class="flex flex-row md:flex-col items-center md:items-start gap-3 md:gap-0.5 rounded-xl bg-red-100 px-4 py-3">
          <span class="font-inter font-bold text-3xl text-red min-w-[72px] md:min-w-0">
            {{ $t('reservation.cancel_request.visibility_value') }}
          </span>
          <span class="font-inter font-medium text-sm text-grey-900">
            {{ $t('reservation.cancel_request.visibility_label') }}
          </span>
        </div>
      </div>

      <div class="flex flex-col gap-3 rounded-xl bg-grey-50 p-4">
        <span class="font-inter font-normal text-base text-grey-900">
          {{ $t('reservation.cancel_request.platforms') }}
        </span>
        <span class="font-inter font-semibold text-base text-grey-900">
          {{ $t('reservation.cancel_request.consequences_title') }}
        </span>
        <ul class="flex flex-col gap-1.5">
          <li v-for="consequence in consequences" :key="consequence" class="flex items-start gap-2">
            <i class="ga-icon ga-filled_cross text-lg text-grey-400 mt-0.5" />
            <span class="font-inter font-normal text-base text-grey-900">
              <b class="font-inter font-semibold text-base text-grey-900">
                {{ $t('reservation.cancel_request.consequence_' + consequence + '_title') }}
              </b>
              {{ $t('reservation.cancel_request.consequence_' + consequence) }}
            </span>
          </li>
        </ul>
      </div>

      <div class="flex items-start gap-4 rounded-xl border border-secondary-200 bg-secondary-50 p-4">
        <span class="flex items-center justify-center shrink-0 w-10 h-10 rounded-full bg-secondary">
          <i class="ga-icon ga-call text-xl text-white" />
        </span>
        <div class="flex flex-col items-start gap-3">
          <span class="font-inter font-normal text-base text-grey-900">
            <b class="font-inter font-semibold text-base text-grey-900">
              {{ $t('reservation.cancel_request.unexpected_title') }}
            </b>
            {{ $t('reservation.cancel_request.unexpected') }}
          </span>
          <a :href="callHref" class="rounded-md bg-secondary px-3 py-2 font-inter font-medium text-base text-white">
            {{ callLabel }}
          </a>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex flex-col gap-3 w-full px-2 pb-2">
        <div class="flex flex-col gap-1">
          <AkCheckbox
            v-model="understood"
            :label="$t('reservation.cancel_request.understood')"
            label-class="font-inter font-normal text-base text-grey-900 text-left"
            value=""
            @change="changeUnderstood" />
          <span v-if="showUnderstoodError" class="ml-7 font-inter font-medium text-sm text-red text-left">
            {{ $t('reservation.cancel_request.understood_required') }}
          </span>
        </div>
        <div class="flex flex-col-reverse md:flex-row gap-3 mt-2">
          <span class="btn-light-gray flex-1 !py-3" :class="{'opacity-50': !understood}" @click="confirm">
            {{ $t('reservation.cancel_request.confirm') }}
          </span>
          <span class="btn-primary flex-1" @click="hide">{{ $t('reservation.cancel_request.keep') }}</span>
        </div>
      </div>
    </template>
  </AkDialogHeadless>
</template>
